import { z } from 'zod';

import { USER_ROLES } from '../hooks/useUsers';
import { t } from '../i18n/validation.js';
import { UserRole } from '../types/user.js';

const USERNAME_REGEX = /^[a-zA-Z0-9._-]+$/;
const MAX_USER_TAGS = 50;

const tagSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .trim()
    .min(1, t('validation.tag.nameRequired'))
    .max(64, t('validation.tag.nameMaxLength')),
});

export const userFormSchemaBase = z.object({
  username: z
    .string()
    .trim()
    .min(3, t('validation.user.usernameMinLength'))
    .regex(USERNAME_REGEX, t('validation.user.usernameFormat')),
  password: z.string(),
  role: z
    .string({ error: t('validation.user.roleRequired') })
    .refine((value): value is UserRole => USER_ROLES.includes(value as UserRole), {
      message: t('validation.user.roleRequired'),
    })
    .transform((value) => value as UserRole),
  tags: z
    .array(tagSchema)
    .max(MAX_USER_TAGS, t('validation.maxTags', { max: MAX_USER_TAGS }))
    .transform((tags) => tags.map((tag) => ({ ...tag, name: tag.name.trim() }))),
});

export type UserFormValues = z.infer<typeof userFormSchemaBase>;

export const createUserFormSchema = (mode: 'create' | 'edit') =>
  userFormSchemaBase.superRefine((data, ctx) => {
    if (mode === 'create') {
      if (data.password.length < 6) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['password'],
          message: t('validation.user.passwordMinLength'),
        });
      }
    } else if (data.password.length > 0 && data.password.length < 6) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: t('validation.user.passwordMinLength'),
      });
    }
  });
